import FeatherIcon from 'feather-icons-react';

const PROPERTY_TYPE_ICONS = {
  apartment: '🏢',
  villa: '🏡',
  townhouse: '🏘️',
  condo: '🏙️',
  mixed: '🏗️',
};

const LAUNDRY_LABELS = {
  in_suite: 'In-Suite Laundry',
  shared: 'Shared Laundry',
  none: 'No Laundry',
};

const SMOKING_LABELS = {
  non_smoking: 'Non-Smoking',
  outdoor_only: 'Outdoor Only',
  allowed: 'Smoking OK',
};

const formatPrice = (label) => label || 'Contact for pricing';

const KnowledgeBaseList = ({
  entries,
  searchQuery,
  filterCity,
  filterType,
  filterActive,
  onSearchChange,
  onFilterCityChange,
  onFilterTypeChange,
  onFilterActiveChange,
  onCreate,
  onEdit,
  onDelete,
  onToggleActive,
  userRole,
}) => {
  const activeCount = entries.filter(e => e.is_active).length;
  const totalFloorPlans = entries.reduce((sum, e) => sum + (e.floor_plans?.length || 0), 0);
  const totalUnits = entries.reduce((sum, e) => sum + (e.total_units || 0), 0);

  const uniqueCities = [...new Set(entries.map(e => e.city).filter(Boolean))].sort();

  return (
    <div>
      <div className="kb-header">
        <div className="kb-header__left">
          <div className="kb-header__icon">🏛️</div>
          <div>
            <h1 className="kb-header__title">Company Knowledge Base</h1>
            <p className="kb-header__subtitle">
              Manage property data for your AI leasing agent
            </p>
          </div>
        </div>

        <div className="kb-header__actions">
          <div className="kb-header__stat">
            <span className="kb-header__stat-num">{entries.length}</span>
            <span className="kb-header__stat-label">Properties</span>
          </div>
          <div className="kb-header__stat">
            <span className="kb-header__stat-num">{activeCount}</span>
            <span className="kb-header__stat-label">Active</span>
          </div>
          <div className="kb-header__stat">
            <span className="kb-header__stat-num">{totalFloorPlans}</span>
            <span className="kb-header__stat-label">Floor Plans</span>
          </div>
          <div className="kb-header__stat">
            <span className="kb-header__stat-num">{totalUnits}</span>
            <span className="kb-header__stat-label">Total Units</span>
          </div>

          {(userRole === 'company' || userRole === 'manager') && (
            <button className="kb-btn kb-btn--primary" onClick={onCreate}>
              <FeatherIcon icon="plus" size={15} />
              Add Property
            </button>
          )}
        </div>
      </div>

      <div className="kb-toolbar">
        <div className="kb-toolbar__search">
          <FeatherIcon icon="search" size={15} className="kb-toolbar__search-icon" />
          <input
            type="text"
            placeholder="Search properties, cities, addresses…"
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
          />
        </div>

        <div className="kb-toolbar__divider" />

        <div className="kb-toolbar__filter">
          <select value={filterCity} onChange={e => onFilterCityChange(e.target.value)}>
            <option value="">All Cities</option>
            {uniqueCities.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="kb-toolbar__filter">
          <select value={filterType} onChange={e => onFilterTypeChange(e.target.value)}>
            <option value="">All Types</option>
            <option value="apartment">Apartment</option>
            <option value="villa">Villa</option>
            <option value="townhouse">Townhouse</option>
            <option value="condo">Condo</option>
            <option value="mixed">Mixed</option>
          </select>
        </div>

        <div className="kb-toolbar__filter">
          <select value={filterActive} onChange={e => onFilterActiveChange(e.target.value)}>
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="kb-empty">
          <div className="kb-empty__icon">🏛️</div>
          <h3 className="kb-empty__title">No properties found</h3>
          <p className="kb-empty__subtitle">
            {searchQuery || filterCity || filterType || filterActive
              ? 'Try adjusting your filters'
              : 'Add your first property to the knowledge base'}
          </p>
          {!searchQuery && !filterCity && !filterType && !filterActive && (userRole === 'company' || userRole === 'manager') && (
            <button className="kb-btn kb-btn--primary" onClick={onCreate}>
              <FeatherIcon icon="plus" size={15} />
              Add First Property
            </button>
          )}
        </div>
      ) : (
        <div className="kb-grid">
          {entries.map(entry => (
            <PropertyCard
              key={entry._id}
              entry={entry}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleActive={onToggleActive}
              userRole={userRole}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const PropertyCard = ({ entry, onEdit, onDelete, onToggleActive, userRole }) => {
  const icon = PROPERTY_TYPE_ICONS[entry.property_type] || '🏢';
  const areaLabel = entry.area || entry.neighbourhood || null;

  return (
    <div className={`kb-card${!entry.is_active ? ' kb-card--inactive' : ''}`}>
      <div className="kb-card__header">
        <div className={`kb-card__status kb-card__status--${entry.is_active ? 'active' : 'inactive'}`} />
        <div className="kb-card__type-badge">
          {icon} {entry.property_type}
        </div>
        <h3 className="kb-card__name">{entry.property_name}</h3>
        <div className="kb-card__address">
          <FeatherIcon icon="map-pin" size={11} />
          {entry.address}{entry.city ? `, ${entry.city}` : ''}
        </div>
        {areaLabel && (
          <div className="kb-card__area-badge">
            <FeatherIcon icon="layers" size={10} />
            {areaLabel}
          </div>
        )}
      </div>

      <div className="kb-card__body">
        <div className="kb-card__metrics">
          <div className="kb-card__metric">
            <span className="kb-card__metric-val">{entry.total_units || 0}</span>
            <span className="kb-card__metric-label">Total Units</span>
          </div>
          <div className="kb-card__metric">
            <span className="kb-card__metric-val">{entry.available_units_count || 0}</span>
            <span className="kb-card__metric-label">Available</span>
          </div>
          <div className="kb-card__metric">
            <span className="kb-card__metric-val">{entry.floor_plans?.length || 0}</span>
            <span className="kb-card__metric-label">Floor Plans</span>
          </div>
        </div>

        <div className="kb-card__price">
          <span className="kb-card__price-label">Pricing</span>
          <span className="kb-card__price-value">
            {entry.price_range_label || formatPrice(null)}
          </span>
        </div>

        <div className="kb-card__tags">
          {entry.laundry && entry.laundry !== 'none' && (
            <span className="kb-card__tag kb-card__tag--laundry">
              🧺 {LAUNDRY_LABELS[entry.laundry] || entry.laundry}
            </span>
          )}
          {entry.resident_parking?.available && (
            <span className="kb-card__tag kb-card__tag--parking">
              🚗 Parking
            </span>
          )}
          {entry.pet_policy?.pets_allowed && (
            <span className="kb-card__tag kb-card__tag--pets">
              🐾 Pet Friendly
            </span>
          )}
          {entry.smoking_policy && entry.smoking_policy !== 'non_smoking' && (
            <span className="kb-card__tag kb-card__tag--smoking">
              🚬 {SMOKING_LABELS[entry.smoking_policy]}
            </span>
          )}
          {entry.has_upcoming_events && (
            <span className="kb-card__tag kb-card__tag--events">
              🎉 Events
            </span>
          )}
        </div>
      </div>

      <div className="kb-card__footer">
        <span className="kb-card__slug">/{entry.slug}</span>

        <div className="kb-card__actions">
          {(userRole === 'company' || userRole === 'manager') && (
            <button
              className="kb-btn kb-btn--ghost kb-btn--icon kb-btn--sm"
              onClick={() => onToggleActive(entry)}
              title={entry.is_active ? 'Deactivate' : 'Activate'}
            >
              <FeatherIcon icon={entry.is_active ? 'eye-off' : 'eye'} size={14} />
            </button>
          )}
          <button
            className="kb-btn kb-btn--ghost kb-btn--sm"
            onClick={() => onEdit(entry)}
          >
            <FeatherIcon icon="edit-2" size={13} />
            Edit
          </button>
          {userRole === 'company' && (
            <button
              className="kb-btn kb-btn--danger kb-btn--sm"
              onClick={() => onDelete(entry)}
            >
              <FeatherIcon icon="trash-2" size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export { KnowledgeBaseList };